import {
  GenevaSupportingData,
  GenevaSupportingFieldMap,
  GenevaSupportingItemSummary,
  GenevaSupportingMultiRecordPayload,
} from '../types/geneva-schema';

const isMultiRecordPayload = (
  data: GenevaSupportingData
): data is GenevaSupportingMultiRecordPayload => {
  return Boolean(data && typeof data === 'object' && (data as any)._multiRecord === true);
};

const parseSummaryJson = (summary: GenevaSupportingItemSummary): Record<string, any> | null => {
  if (!summary.rawJson) return null;
  try {
    return JSON.parse(summary.rawJson);
  } catch (error) {
    console.warn('Failed to parse supporting item JSON', error);
    return null;
  }
};

export const normalizeSupportingRecords = (
  data?: GenevaSupportingData | null
): Record<string, GenevaSupportingFieldMap> => {
  if (!data || typeof data !== 'object') {
    return {};
  }

  if (isMultiRecordPayload(data)) {
    return data.records || {};
  }

  return data as Record<string, GenevaSupportingFieldMap>;
};

export const getSummaryDetails = (
  summary: GenevaSupportingItemSummary
): {
  code: string | null;
  name: string | null;
  description: string | null;
  parsed?: Record<string, any> | null;
} => {
  const parsed = parseSummaryJson(summary);

  const code = summary.code
    ?? parsed?.CODE
    ?? parsed?.NAMESORT
    ?? (typeof parsed?.MAKENAME === 'string' ? parsed!.MAKENAME.replace(/^Portfolio\[|\]$/g, '') : null);

  const description = summary.description
    ?? parsed?.DESCRIPTION
    ?? parsed?.OBJECTDESCRIPTION
    ?? parsed?.MAKENAME
    ?? null;

  const name = summary.name
    ?? parsed?.NAMELINE1
    ?? parsed?.OBJECTDESCRIPTION
    ?? parsed?.DESCRIPTION
    ?? parsed?.MAKENAME
    ?? description
    ?? null;

  return { code: code ?? null, name: name ?? null, description: description ?? null, parsed };
};

const normalizeValue = (value?: string | null) => value?.trim().toLowerCase() || null;

export const summaryMatchesCode = (summary: GenevaSupportingItemSummary, code?: string | null): boolean => {
  if (!code) return false;
  const normalizedCode = normalizeValue(code);
  if (!normalizedCode) return false;

  const details = getSummaryDetails(summary);
  const candidates = [
    summary.code,
    summary.name,
    summary.description,
    details.code,
    details.name,
    details.description,
  ].map(normalizeValue).filter(Boolean);

  if (candidates.includes(normalizedCode)) {
    return true;
  }

  if (summary.rawJson) {
    const parsed = parseSummaryJson(summary);
    if (parsed) {
      const extraFields = [
        parsed.CODE,
        parsed.NAMESORT,
        parsed.MAKENAME,
        parsed.Portfolio,
        parsed.portfolio,
      ];
      if (extraFields.map(normalizeValue).includes(normalizedCode)) {
        return true;
      }
    }
  }

  return false;
};

export const summaryMatchesQuery = (summary: GenevaSupportingItemSummary, query: string): boolean => {
  if (!query) return false;
  const normalizedQuery = normalizeValue(query);
  if (!normalizedQuery) return false;

  const details = getSummaryDetails(summary);
  const candidates = [
    summary.code,
    summary.name,
    summary.description,
    details.code,
    details.name,
    details.description,
  ].map(normalizeValue).filter(Boolean);

  if (candidates.some((value) => value && value.includes(normalizedQuery))) {
    return true;
  }

  if (summary.rawJson) {
    const parsed = parseSummaryJson(summary);
    if (parsed) {
      return Object.values(parsed).some((value) =>
        typeof value === 'string' && normalizeValue(value)?.includes(normalizedQuery)
      );
    }
  }

  return false;
};

export const collectFieldSummaries = (
  data: GenevaSupportingData | undefined,
  fieldName: string
): GenevaSupportingItemSummary[] => {
  if (!fieldName) return [];
  const target = fieldName.trim().toLowerCase();
  const records = normalizeSupportingRecords(data);

  return Object.values(records).flatMap((fields) =>
    Object.entries(fields)
      .filter(([key]) => key.trim().toLowerCase() === target)
      .map(([, summary]) => summary)
  );
};

export const getDisplayNameFromSummary = (summary: GenevaSupportingItemSummary): string | null => {
  const details = getSummaryDetails(summary);
  return details.name || details.description;
};

export const getDisplayCodeFromSummary = (summary: GenevaSupportingItemSummary): string | null => {
  const details = getSummaryDetails(summary);
  return details.code;
};

export const flattenSupportingSummaries = (
  data?: GenevaSupportingData | null
): GenevaSupportingItemSummary[] => {
  const records = normalizeSupportingRecords(data || undefined);
  return Object.values(records).flatMap((fields) => Object.values(fields));
};

